 
import React from "react";
import { PageLoader } from "../../../../../../components/PageLoader/PageLoader";
import { StandadCard } from "../../../../../../components/Cards";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import { LoadState, MODULE_IDS } from "../../../../../../constants/enums";
import { useDispatchWrapper } from "../../../../../../hooks";
import { Navigate } from "react-router-dom";
import { useFormik } from "formik";
import { Grid, Stack, Typography } from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../components/formsComponents";

import {
  IBusinessPolicyCoverage,
  ICoverageRow,
} from "../../../../../../redux/businessPolicy/businessPolicyCoverage/businessPolicyCoverage.types";
import produce from "immer";
import {
  addBusinesPolicyCoverageAsync,
  clearBusinessPolicyCoverage,
  fetchBusinessPolicyCoverageAsync,
  fetchLeadBusinessPolicyCoverageAsync,
  upsertLeadBusinessPolicyCoverageAsync,
} from "../../../../../../redux/businessPolicy/businessPolicyCoverage/businesPolicyCoverageActions";
import { CoveragePropertyTable } from "./CoveragePropertyTable";
import { CoverageBusinessInterruptionTable } from "./CoverageBusinessInterruption";
import { CoverageMachineryBreakdownTable } from "./CoverageMachineryBreakdown";
import { CoverageCrimeTable } from "./CoverageCrime";
import { CoverageLiability } from "./CoverageLiability";
import { CoverageUmbrellaLiability } from "./CoverageUmbrellaLiability";
import { CoverageExcessLiability } from "./CoverageExcesssLiability";
import { CoverageOther } from "./CoverageOther";
import { getUniqueId } from "../../../../../../helpers";
import { RoleBasedCustomButton } from "../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { batch } from "react-redux";
import {
  fetchBusinessPolicyRiskListAsync,
  fetchLeadBusinessRiskListAsync,
} from "../../../../../../redux/businessPolicy/buisenessPolicyRiskLocation/businessPolicyRiskLocationActions";
import { COMMON_STATUS } from "../../../../../../constants/constants";
import { FieldSet } from "../../../../../../components/FieldSet/FieldSet";
import { PageContainer } from "../../../../../../components/container/PageContainer";

export type SectionKey =
  | "property"
  | "business_interruption"
  | "machinery_breakdown"
  | "crime"
  | "liability"
  | "umbrella_liability"
  | "excess_liability"
  | "other";

interface ICoverageProps {
  customerId?: number;
  customerPolicyId?: number;
  /** Lead-scoped identifier. When present, the form saves/fetches by lead_id. */
  leadId?: string;
  coverageId?: number;
  readOnly?: boolean;
  fromRightPanel?: boolean;
  staticStatusValue?: string;
  created_from?: string;
  onSaveSuccess: (riskLocation: IBusinessPolicyCoverage) => void;
  initialData?: IBusinessPolicyCoverage;
  disableApiCalls?: boolean;
  disableSaveApiCall?: boolean;
}

export const Coverage: React.FC<ICoverageProps> = (props) => {
  const {
    customerId,
    customerPolicyId,
    leadId,
    coverageId,
    onSaveSuccess,
    created_from = "Coverage",
    fromRightPanel = false,
    readOnly = false,
    staticStatusValue,
    initialData,
    disableApiCalls = false,
    disableSaveApiCall = false,
  } = props;

  const { coverageLoading, coverage, error } = useSelector(
    (storeState: IStoreState) => storeState.business.coverage
  );
  const dispatch = useDispatchWrapper();
  const [saveLoading, setSaveLoading] = React.useState(false);
  const riskLocations = useSelector(
    (storeState: IStoreState) => storeState.business.riskLocation.data
  );

  const { values, handleChange, handleSubmit, setFieldValue, setValues } =
    useFormik({
      initialValues: initialData ? initialData : coverage,
      validate: (values) => {},
      onSubmit: (values) => {
        if (disableSaveApiCall) {
          onSaveSuccess(values);
          return;
        }
        let finalStatus = values.status;
        if (staticStatusValue) {
          finalStatus = staticStatusValue;
        }
        // Lead-binder scope: save by lead_id (no customer_policy_id required).
        if (leadId) {
          setSaveLoading(true);
          dispatch(
            upsertLeadBusinessPolicyCoverageAsync(
              {
                ...values,
                status: finalStatus,
                created_from: created_from,
              },
              leadId,
              (isSuccess, data) => {
                if (isSuccess && data) {
                  window.scrollTo(0, 0);
                  onSaveSuccess(data);
                }
                setSaveLoading(false);
              }
            )
          );
          return;
        }
        if (customerPolicyId && customerId) {
          setSaveLoading(true);
          dispatch(
            addBusinesPolicyCoverageAsync(
              {
                ...values,
                customer_policy_id: customerPolicyId,
                status: finalStatus,
                created_from: created_from,
              },
              (isSuccess, data) => {
                if (isSuccess && data) {
                  window.scrollTo(0, 0);
                  onSaveSuccess(data);
                }
                setSaveLoading(false);
              }
            )
          );
        }
      },
    });

  // Keep a ref to the latest values so callbacks remain stable
  const valuesRef = React.useRef(values);
  valuesRef.current = values;

  React.useEffect(() => {
    if (disableApiCalls || !coverageId) {
      return;
    }
    if (leadId) {
      dispatch(fetchLeadBusinessPolicyCoverageAsync(leadId, coverageId));
    } else if (customerPolicyId) {
      dispatch(fetchBusinessPolicyCoverageAsync(customerPolicyId, coverageId));
    }
  }, [customerPolicyId, leadId, coverageId]);

  React.useEffect(() => {
    if (initialData && disableApiCalls) {
      return;
    }
    setValues({ ...coverage, coverage_number: coverageId || null });
  }, [coverage]);

  React.useEffect(() => {
    return () => {
      dispatch(clearBusinessPolicyCoverage());
    };
  }, []);

  const handleRowChange = React.useCallback(
    (sectionKey: SectionKey, fieldKey: keyof ICoverageRow, rowIndex: number, value: string) => {
      const currentRows = valuesRef.current[sectionKey];
      if (currentRows.length > rowIndex) {
        const finalRows = produce(currentRows, (draftRows) => {
          draftRows[rowIndex][fieldKey] = value;
        });
        setFieldValue(sectionKey, finalRows);
      }
    },
    [setFieldValue]
  );

  const handleAddRow = React.useCallback(
    (sectionKey: SectionKey, currentRowIndex: number) => {
      const row: ICoverageRow = {
        key: getUniqueId(),
        type: "",
        co_insurance: "",
        deductible: "",
        limit: "",
        rate: "",
        premium: "",
      };
      const finalValues = [...valuesRef.current[sectionKey]];
      finalValues.splice(currentRowIndex + 1, 0, row);
      setFieldValue(sectionKey, finalValues);
    },
    [setFieldValue]
  );

  const handleRemoveRow = React.useCallback(
    (sectionKey: SectionKey, index: number) => {
      const finalRows = valuesRef.current[sectionKey].filter((_, i) => i !== index);
      setFieldValue(sectionKey, finalRows);
    },
    [setFieldValue]
  );

  React.useEffect(() => {
    if (leadId) {
      dispatch(fetchLeadBusinessRiskListAsync({ leadId }, "ACTIVE"));
    } else if (customerPolicyId) {
      batch(() => {
        dispatch(fetchBusinessPolicyRiskListAsync(customerPolicyId, "ACTIVE"));
      });
    }
  }, []);

  // Stable per-section callbacks to avoid breaking React.memo on child tables
  const propertyHandlers = React.useMemo(() => ({
    onRowChange: (key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("property", key, rowIndex, value),
    onAddRow: (currentRowIndex: number) => handleAddRow("property", currentRowIndex),
    onRemoveRow: (index: number) => handleRemoveRow("property", index),
  }), [handleRowChange, handleAddRow, handleRemoveRow]);

  const businessInterruptionHandlers = React.useMemo(() => ({
    onRowChange: (key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("business_interruption", key, rowIndex, value),
    onAddRow: (currentRowIndex: number) => handleAddRow("business_interruption", currentRowIndex),
    onRemoveRow: (index: number) => handleRemoveRow("business_interruption", index),
  }), [handleRowChange, handleAddRow, handleRemoveRow]);

  const machineryBreakdownHandlers = React.useMemo(() => ({
    onRowChange: (key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("machinery_breakdown", key, rowIndex, value),
    onAddRow: (currentRowIndex: number) => handleAddRow("machinery_breakdown", currentRowIndex),
    onRemoveRow: (index: number) => handleRemoveRow("machinery_breakdown", index),
  }), [handleRowChange, handleAddRow, handleRemoveRow]);

  const crimeHandlers = React.useMemo(() => ({
    onRowChange: (key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("crime", key, rowIndex, value),
    onAddRow: (currentRowIndex: number) => handleAddRow("crime", currentRowIndex),
    onRemoveRow: (index: number) => handleRemoveRow("crime", index),
  }), [handleRowChange, handleAddRow, handleRemoveRow]);

  const liabilityHandlers = React.useMemo(() => ({
    onRowChange: (key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("liability", key, rowIndex, value),
    onAddRow: (currentRowIndex: number) => handleAddRow("liability", currentRowIndex),
    onRemoveRow: (index: number) => handleRemoveRow("liability", index),
  }), [handleRowChange, handleAddRow, handleRemoveRow]);

  const umbrellaLiabilityHandlers = React.useMemo(() => ({
    onRowChange: (key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("umbrella_liability", key, rowIndex, value),
    onAddRow: (currentRowIndex: number) => handleAddRow("umbrella_liability", currentRowIndex),
    onRemoveRow: (index: number) => handleRemoveRow("umbrella_liability", index),
  }), [handleRowChange, handleAddRow, handleRemoveRow]);

  const excessLiabilityHandlers = React.useMemo(() => ({
    onRowChange: (key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("excess_liability", key, rowIndex, value),
    onAddRow: (currentRowIndex: number) => handleAddRow("excess_liability", currentRowIndex),
    onRemoveRow: (index: number) => handleRemoveRow("excess_liability", index),
  }), [handleRowChange, handleAddRow, handleRemoveRow]);

  const otherHandlers = React.useMemo(() => ({
    onRowChange: (key: keyof ICoverageRow, rowIndex: number, value: string) => handleRowChange("other", key, rowIndex, value),
    onAddRow: (currentRowIndex: number) => handleAddRow("other", currentRowIndex),
    onRemoveRow: (index: number) => handleRemoveRow("other", index),
  }), [handleRowChange, handleAddRow, handleRemoveRow]);
  const riskLocationsDropDown = React.useMemo(() => {
    return riskLocations.map((item) => {
      return {
        label:
          item.location_number +
          ` (${item.address}, ${item.city}, ${item.province_or_state}, ${item.postal_code})`,
        value: item.location_number || "",
      };
    });
  }, [riskLocations]);

  // In the lead-binder scope there is no customer_policy_id (the form is keyed by
  // lead_id). Only redirect when neither a customer policy nor a lead scope is present.
  if (!customerPolicyId && !leadId) {
    return <Navigate to={"/customers-list"} />;
  }

  return (
    <PageContainer title={coverageId
        ? "Edit Coverage"
            : "Create Coverage"
    } description="this is inner page">
    <PageLoader
      loading={coverageLoading === LoadState.InProgress}
      error={error ? { message: error } : undefined}
    >
      <StandadCard
        sx={{ mt: 2 }}
        renderWithoutCard={fromRightPanel}
        heading={!fromRightPanel ? "Coverage Summary" : ""}
      >
        <form onSubmit={handleSubmit}>
          <FieldSet disabled={readOnly}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Coverage Number</CustomFormLabel>
                <CustomTextField
                  name="coverage_number"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  disabled
                  value={values.coverage_number}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomFormLabel>Risk Location</CustomFormLabel>

                <ControlledCustomSelect
                  variant="outlined"
                  size="small"
                  type="string"
                  name="risk_location"
                  fullWidth
                  displayEmpty
                  value={values.risk_location}
                  onChange={handleChange}
                  placeholder="Select One"
                  options={riskLocationsDropDown}
                ></ControlledCustomSelect>
              </Grid>
              <Grid item xs={12} lg={3}>
                <CustomFormLabel>Status</CustomFormLabel>
                <ControlledCustomSelect
                  fullWidth
                  value={values.status}
                  name="status"
                  onChange={handleChange}
                  placeholder="Select one"
                  displayEmpty
                  options={COMMON_STATUS}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 3 }}>PROPERTY</Typography>
            <CoveragePropertyTable
              rows={values.property}
              onAddRow={propertyHandlers.onAddRow}
              onRemoveRow={propertyHandlers.onRemoveRow}
              onRowChange={propertyHandlers.onRowChange}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>BUSINESS INTERRUPTION</Typography>
            <CoverageBusinessInterruptionTable
              rows={values.business_interruption}
              onAddRow={businessInterruptionHandlers.onAddRow}
              onRemoveRow={businessInterruptionHandlers.onRemoveRow}
              onRowChange={businessInterruptionHandlers.onRowChange}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>MACHINERY BREAKDOWN</Typography>
            <CoverageMachineryBreakdownTable
              rows={values.machinery_breakdown}
              onAddRow={machineryBreakdownHandlers.onAddRow}
              onRemoveRow={machineryBreakdownHandlers.onRemoveRow}
              onRowChange={machineryBreakdownHandlers.onRowChange}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>CRIME</Typography>
            <CoverageCrimeTable
              rows={values.crime}
              onAddRow={crimeHandlers.onAddRow}
              onRemoveRow={crimeHandlers.onRemoveRow}
              onRowChange={crimeHandlers.onRowChange}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>LIABILITY</Typography>
            <CoverageLiability
              rows={values.liability}
              onAddRow={liabilityHandlers.onAddRow}
              onRemoveRow={liabilityHandlers.onRemoveRow}
              onRowChange={liabilityHandlers.onRowChange}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>UMBRELLA LIABILITY</Typography>
            <CoverageUmbrellaLiability
              rows={values.umbrella_liability}
              onAddRow={umbrellaLiabilityHandlers.onAddRow}
              onRemoveRow={umbrellaLiabilityHandlers.onRemoveRow}
              onRowChange={umbrellaLiabilityHandlers.onRowChange}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>EXCESS LIABILITY</Typography>
            <CoverageExcessLiability
              rows={values.excess_liability}
              onAddRow={excessLiabilityHandlers.onAddRow}
              onRemoveRow={excessLiabilityHandlers.onRemoveRow}
              onRowChange={excessLiabilityHandlers.onRowChange}
            />

            <Typography variant="h6" sx={{ mt: 3 }}>OTHER</Typography>
            <CoverageOther
              rows={values.other}
              onAddRow={otherHandlers.onAddRow}
              onRemoveRow={otherHandlers.onRemoveRow}
              onRowChange={otherHandlers.onRowChange}
            />

            <Stack direction="row" spacing={3} sx={{ mt: 4 }}>
              <RoleBasedCustomButton
                moduleKey={MODULE_IDS.BUSINESS_POLICY}
                disabled={saveLoading}
                variant="contained"
                type="submit"
              >
                Save
              </RoleBasedCustomButton>
            </Stack>
          </FieldSet>
        </form>
      </StandadCard>
    </PageLoader>
    </PageContainer>
  );
};
